import React, { useEffect } from 'react'

import { incrementLoadingCounter } from '@/entities/userInterface/slices/loaderSlice'
import { isTokenExpired } from '@/shared/lib/JWTTokenHelpers'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { alpha, Box, keyframes, styled, Typography } from '@mui/material'

export type LoadingScreenProps = {
  onLoadingComplete?: () => void
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ onLoadingComplete }) => {
  const [isLoaderHidden, setIsLoaderHidden] = React.useState(false)
  const dispatch = useAppDispatch()
  const session = useAppSelector(state => state.session)
  const { counter, isComplete } = useAppSelector(state => state.loader)

  // Animate loader
  useEffect(() => {
    const isSessionExpired = isTokenExpired(session.accessToken) || session.token === null

    if (isSessionExpired) {
      setIsLoaderHidden(true)

      return
    }

    let timeoutId: number

    const startCounter = () => {
      if (!isComplete) {
        dispatch(incrementLoadingCounter())
        timeoutId = window.setTimeout(startCounter, 20)
      }
    }

    startCounter()
    setIsLoaderHidden(isComplete)

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isComplete, dispatch, session])

  useEffect(() => {
    if (isComplete) {
      const timeoutId = window.setTimeout(() => {
        onLoadingComplete?.()
        setIsLoaderHidden(true)
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [isComplete, onLoadingComplete])

  return (
    <LoadingOverlay
      sx={{ display: isLoaderHidden ? 'none' : 'flex' }}
    >
      <LogoContainer>
        <Logo src="/aside-logo.svg" alt="Logo" />
        <LogoRevealMask sx={{ transform: `translateX(${counter}%)` }} />
      </LogoContainer>
      <ProgressBar>
        <ProgressBarInner style={{ width: `${counter}%` }} />
      </ProgressBar>
      <DotsContainer>
        <LoadingDot delay={0} />
        <LoadingDot delay={0.2} />
        <LoadingDot delay={0.4} />
      </DotsContainer>

      <LoadingText variant="body2">
        Loading...
      </LoadingText>
    </LoadingOverlay>
  )
}


const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
`

const LoadingOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.palette.custom.sidebar.backgroundColor};
  z-index: ${props => props.theme.zIndex.modal + 1};
`

const LogoContainer = styled(Box)`
  position: relative;
  margin-bottom: ${props => props.theme.spacing(4)};
`

const Logo = styled('img')`
  height: ${(props) => props.theme.typography.pxToRem(192)};
  margin: ${(props) => props.theme.typography.pxToRem(16)} auto;
`

const LogoRevealMask = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => alpha(props.theme.palette.custom.sidebar.backgroundColor, 0.5)};
  transition: transform 0.2s ease-out;
`

const DotsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(2)};
`

const LoadingDot = styled(Box) <{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #63CCC9;
  animation: ${bounce} 1.4s infinite ease-in-out;
  animation-delay: ${props => props.delay}s;
`

const LoadingText = styled(Typography)`
  color: ${props => props.theme.palette.custom.sidebar.textDimColor};
  opacity: 0.5;
`

const ProgressBar = styled(Box)`
  width: 200px;
  height: 4px;
  background: ${props => alpha(props.theme.palette.primary.main, 0.2)};
  border-radius: 2px;
  margin: ${props => props.theme.spacing(2)} 0;
  overflow: hidden;
`

const ProgressBarInner = styled(Box)`
  height: 100%;
  background: ${props => props.theme.palette.primary.main};
  transition: width 0.2s ease-out;
`